import { omit } from 'lodash-es'
import { getSnapshot, types } from 'mobx-state-tree'
import { isEmail, isLength } from 'validator'

import { FormStringFieldModel } from '../FormField/FormFieldModel'
import { IFormValidation } from '../FormField/FormValidation'

// eslint-disable-next-line no-useless-escape
const optionalPhoneNumberRegex = /^([0-9()\-+\ ]{7,25})?$/ // for optional phone numbers

export interface IContactResJSON {
  data: IContactJSON[]
  total: number
}

export type ContactV2DTO = {
  id: string
  firstName: string
  lastName: string
  email: string
  mobileNumber: string
}

export interface IContactJSON {
  _id: string
  organizationId: string
  customerId: string
  name: string
  email: string
  phone?: string
  companyRole?: string
  notes: string
  createdAt: string
  updatedAt?: string
  deletedAt?: string
}

const dirtyEnabledFields = ['organizationId', 'name', 'email', 'phone', 'companyRole', 'notes']

export const Contact = types
  .model({
    _id: types.maybe(types.identifier(types.string)),
    customerId: types.maybe(types.string),
    organizationId: types.optional(FormStringFieldModel, undefined),
    name: FormStringFieldModel,
    email: FormStringFieldModel,
    phone: FormStringFieldModel,
    companyRole: FormStringFieldModel,
    notes: FormStringFieldModel,
    createdAt: types.maybe(types.string),
    updatedAt: types.maybe(types.string),
    deletedAt: types.maybe(types.string),
  })
  .views((self) => ({
    // validation views
    validateOrganizationId(): IFormValidation {
      return {
        isInvalid: self.organizationId.isDirty && !self.organizationId.value,
        message: 'directory-validation_organization_select',
      }
    },
    validateName(): IFormValidation {
      return {
        isInvalid: self.name.isDirty && !isLength(self.name.value, { min: 1, max: 255 }),
        message: 'directory-validation_name',
      }
    },
    validateEmail(): IFormValidation {
      return {
        isInvalid: self.email.isDirty && !isEmail(self.email.value),
        message: 'directory-validation_email',
      }
    },
    validatePhone(): IFormValidation {
      return {
        isInvalid: self.phone.isDirty && !optionalPhoneNumberRegex.test(self.phone.value),
        message: 'directory-validation_phone',
      }
    },
    validateCompanyRole(): IFormValidation {
      return {
        isInvalid: !isLength(self.companyRole.value, { min: 0, max: 255 }),
        message: 'directory-validation_company_role',
      }
    },
    validateNotes(): IFormValidation {
      return {
        isInvalid: !isLength(self.notes.value, { min: 0, max: 500 }),
        message: 'directory-validation_notes',
      }
    },
  }))
  .views((self) => ({
    get isValid(): boolean {
      const validationField = [
        'validateOrganizationId',
        'validateName',
        'validateEmail',
        'validatePhone',
        'validateCompanyRole',
        'validateNotes',
      ]

      return (
        self.organizationId.isDirty &&
        self.name.isDirty &&
        self.email.isDirty &&
        validationField.every((field) => !self[field]().isInvalid)
      )
    },
    get contactJSON(): Partial<IContactJSON> {
      return getSnapshot(self)
    },
  }))
  .actions((self) => ({
    postProcessSnapshot(snapshot) {
      const fieldsToRemove = ['createdAt', 'updatedAt', 'deletedAt']
      if (snapshot._id) {
        return omit(snapshot, fieldsToRemove)
      }
      return omit(snapshot, [...fieldsToRemove, '_id'])
    },
    setOrganizationId(id: string): void {
      self.organizationId.setValue(id)
    },
    setName(name: string): void {
      self.name.setValue(name)
    },
    setEmail(email: string): void {
      self.email.setValue(email)
    },
    setPhone(phone: string): void {
      self.phone.setValue(phone)
    },
    setCompanyRole(companyRole: string): void {
      self.companyRole.setValue(companyRole)
    },
    setNotes(notes: string): void {
      self.notes.setValue(notes)
    },
    submit(): void {
      dirtyEnabledFields.forEach((dirtyEnabledField) => {
        self[dirtyEnabledField].isDirty = true
      })
    },
  }))

export const ContactsModel = types.model('ContactsModel', {
  data: types.array(Contact),
  total: types.number,
})

export type TContact = typeof Contact.Type
export type TContactsModel = typeof ContactsModel.Type
